/**
 * Compression vidéo côté navigateur via ffmpeg.wasm (cœur mono-thread).
 *
 * - Le cœur (~25 Mo de WASM) est téléchargé À LA DEMANDE depuis un CDN la
 *   première fois qu'on compresse une vidéo : il n'alourdit donc pas le bundle
 *   de l'application et n'impacte que l'utilisateur qui envoie une vidéo lourde.
 * - On utilise le cœur MONO-THREAD pour éviter d'exiger les en-têtes
 *   COOP/COEP (SharedArrayBuffer) sur l'hébergement, qui casseraient les
 *   ressources tierces (YouTube, Cloudinary…).
 *
 * Stratégie : on ré-encode en H.264/AAC (mp4) en visant une taille cible.
 * Deux passes de qualité décroissante (720p puis 480p) ; si le résultat
 * dépasse encore la cible, on lève une erreur → l'appelant retombe sur un
 * refus « vidéo trop lourde ».
 */

const CORE_VERSION = '0.12.10';
const CDN_BASE = `https://unpkg.com/@ffmpeg/core@${CORE_VERSION}/dist/umd`;

let ffmpegInstance = null;
let loadPromise = null;

/** Vrai si le fichier est une vidéo. */
export const isVideoFile = (file) => (file?.type || '').startsWith('video/');

/** Charge (une seule fois) l'instance ffmpeg avec son cœur distant. */
async function getFFmpeg() {
  if (ffmpegInstance) return ffmpegInstance;
  if (!loadPromise) {
    loadPromise = (async () => {
      const [{ FFmpeg }, { toBlobURL }] = await Promise.all([
        import('@ffmpeg/ffmpeg'),
        import('@ffmpeg/util')
      ]);
      const ffmpeg = new FFmpeg();
      await ffmpeg.load({
        coreURL: await toBlobURL(`${CDN_BASE}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${CDN_BASE}/ffmpeg-core.wasm`, 'application/wasm')
      });
      ffmpegInstance = ffmpeg;
      return ffmpeg;
    })();
  }
  return loadPromise;
}

/**
 * Compresse une vidéo pour viser `targetMB` mégaoctets.
 * @param {File} file               Fichier vidéo source.
 * @param {object} [opts]
 * @param {number} [opts.targetMB]  Taille cible en Mo (def. 40).
 * @param {(pct:number)=>void} [opts.onProgress] Progression 0-100.
 * @returns {Promise<File>} Fichier mp4 compressé (≤ cible).
 * @throws {Error} Si ffmpeg ne charge pas, ou si le résultat reste > cible.
 */
export async function compressVideo(file, { targetMB = 40, onProgress } = {}) {
  const { fetchFile } = await import('@ffmpeg/util');
  const ffmpeg = await getFFmpeg();

  const inputName = 'input';
  const outputName = 'output.mp4';
  const targetBytes = targetMB * 1024 * 1024;

  const onProg = ({ progress }) => {
    if (onProgress) onProgress(Math.max(0, Math.min(99, Math.round(progress * 100))));
  };
  ffmpeg.on('progress', onProg);

  // Passes successives : 720p puis 480p, qualité décroissante.
  const passes = [
    ['-vf', 'scale=-2:720', '-c:v', 'libx264', '-crf', '28', '-preset', 'veryfast', '-c:a', 'aac', '-b:a', '128k', '-movflags', '+faststart'],
    ['-vf', 'scale=-2:480', '-c:v', 'libx264', '-crf', '30', '-preset', 'veryfast', '-c:a', 'aac', '-b:a', '96k', '-movflags', '+faststart']
  ];

  let best = null;
  try {
    await ffmpeg.writeFile(inputName, await fetchFile(file));
    for (const args of passes) {
      await ffmpeg.exec(['-i', inputName, ...args, '-y', outputName]);
      const data = await ffmpeg.readFile(outputName);
      const blob = new Blob([data.buffer], { type: 'video/mp4' });
      if (!best || blob.size < best.size) best = blob;
      if (blob.size <= targetBytes) break;
    }
  } finally {
    ffmpeg.off('progress', onProg);
    try { await ffmpeg.deleteFile(inputName); } catch (e) { /* ignore */ }
    try { await ffmpeg.deleteFile(outputName); } catch (e) { /* ignore */ }
  }

  if (!best || best.size > targetBytes) {
    const mb = best ? Math.round(best.size / 1048576) : 0;
    throw new Error(`Vidéo encore trop lourde après compression (${mb} Mo).`);
  }
  if (onProgress) onProgress(100);
  const baseName = (file.name || 'video').replace(/\.[^.]+$/, '');
  return new File([best], `${baseName}.mp4`, { type: 'video/mp4' });
}
